import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Stack,
} from "@mui/material";
import axios from "../api/axios";
import CommonTable from "../components/CommonTable";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";

const UserManagement = ({}) => {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    username: "",
    password: "",
    name: "",
    email: "",
    role: "",
    organization: null,
    branches: [],
  });
  const [errors, setErrors] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  useEffect(() => {
    fetchUsers();
    fetchOrgs();
  }, []);

  const fetchUsers = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get("/users", {
      headers: { "x-auth-token": token },
    });
    setUsers(res.data);
  };

  const fetchOrgs = async () => {
    const token = localStorage.getItem("token");
    const res = await axios.get("/orgs", {
      headers: { "x-auth-token": token },
    });
    setOrgs(res.data);
  };

  const fetchBranches = async (orgId) => {
    const token = localStorage.getItem("token");
    if (!orgId) return setBranches([]);
    const res = await axios.get(`/branches?org=${orgId}`, {
      headers: { "x-auth-token": token },
    });
    setBranches(res.data);
  };

  const resetForm = () => {
    setForm({
      username: "",
      password: "",
      name: "",
      email: "",
      role: "",
      organization: null,
      branches: [],
    });
    setErrors({});
    setEditingUser(null);
  };

  const openForm = (user = null) => {
    if (user) {
      setEditingUser(user);
      setForm({
        username: user.username,
        password: "",
        name: user.name,
        email: user.email,
        role: user.role,
        organization: user.organization,
        branches: user.branches,
      });
      fetchBranches(user.organization?._id);
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const closeForm = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "organization") {
      fetchBranches(value?._id);
      setForm((prev) => ({ ...prev, branches: [] }));
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!form.username) errs.username = "Username required";
    if (!editingUser && !form.password) errs.password = "Password required";
    if (form.password && form.password.length < 8)
      errs.password = "Mininum 8 characters required";
    if (!form.role) errs.role = "Role required";
    if (form.role !== "super_admin" && !form.organization)
      errs.organization = "Organization required";
    if (form.role === "branch_manager" && form.branches.length === 0)
      errs.branches = "Select at least 1 branch";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      username: form.username,
      password: form.password || undefined,
      name: form.name,
      email: form.email,
      role: form.role,
      organization: form.organization?._id || null,
      branches: form.branches.map((b) => b._id),
    };

    const token = localStorage.getItem("token");

    try {
      if (editingUser) {
        await axios.put(`/users/${editingUser._id}`, payload, {
          headers: { "x-auth-token": token },
        });
        showMessage("User updated", "success");
      } else {
        await axios.post("/users", payload, {
          headers: { "x-auth-token": token },
        });
        showMessage("User created", "success");
      }
      fetchUsers();
      closeForm();
    } catch (err) {
      console.error(err);
      showMessage("Error saving user", "error");
    }
  };

  const handleDeactivate = async (id) => {
    const token = localStorage.getItem("token");
    const result = await confirm({
      message: "Are you sure you want to deactivate this user?",
    });
    if (result) {
      await axios.delete(`/users/${id}`, {
        headers: { "x-auth-token": token },
      });
      fetchUsers();
      showMessage("User deactivated", "success");
    }
  };

  return (
    <Box p={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h5">User Management</Typography>
        <Button variant="contained" onClick={() => openForm()}>
          Register New User
        </Button>
      </Stack>

      <CommonTable
        columns={[
          { label: "Username", field: "username" },
          { label: "Name", field: "name" },
          { label: "Role", field: "role" },
          {
            label: "Org",
            field: "organization",
            getSearchValue: (u) => u.organization?.name || "-",
            renderCell: (u) => u.organization?.name || "-",
          },
          {
            label: "Branches",
            getSearchValue: (u) =>
              u.branches?.map((b) => b.name).join(", ") || "-",
            renderCell: (u) => u.branches?.map((b) => b.name).join(", ") || "-",
          },
          {
            label: "Actions",
            sortable: false,
            renderCell: (u) => (
              <>
                <Button size="small" onClick={() => openForm(u)}>
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeactivate(u._id)}
                >
                  Deactivate
                </Button>
              </>
            ),
          },
        ]}
        rows={users}
      />

      <Dialog open={openDialog} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? "Update User" : "Register New User"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => handleChange("username", e.target.value)}
              error={!!errors.username}
              helperText={errors.username}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              fullWidth
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
            />
            <FormControl fullWidth error={!!errors.role}>
              <InputLabel>Role</InputLabel>
              <Select
                value={form.role}
                onChange={(e) => handleChange("role", e.target.value)}
              >
                {/* <MenuItem value="super_admin">Super Admin</MenuItem> */}
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="branch_manager">Branch Manager</MenuItem>
              </Select>
              {errors.role && (
                <Typography variant="caption" color="error">
                  {errors.role}
                </Typography>
              )}
            </FormControl>

            {form.role !== "super_admin" && (
              <Autocomplete
                options={orgs}
                getOptionLabel={(o) => o.name}
                value={form.organization}
                onChange={(e, val) => handleChange("organization", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    error={!!errors.organization}
                    helperText={errors.organization}
                  />
                )}
              />
            )}

            {form.role === "branch_manager" && (
              <Autocomplete
                multiple
                options={branches}
                getOptionLabel={(b) => b.name}
                value={form.branches}
                onChange={(e, val) => handleChange("branches", val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Branches"
                    error={!!errors.branches}
                    helperText={errors.branches}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingUser ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
